import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bell,
  Plus,
  Trash,
  Check,
  Clock,
  X,
  ArrowCounterClockwise,
  ArrowsClockwise,
  Pause,
  Play,
  SkipForward,
  PencilSimple,
  Pause as PauseIcon,
  User,
  ChatCircleText,
} from "@phosphor-icons/react";
import * as API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import ItemMenu from "../components/ItemMenu";
import { toast } from "sonner";
import { useDraft } from "../hooks/useDraft";
import { ScheduleActivityLog } from "../components/ScheduleActivityLog";

function fmtRelative(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (d - now) / 1000;
    const abs = Math.abs(diff);
    const sign = diff < 0 ? "ago" : "in";
    const fmt = (n, u) => `${sign === "ago" ? n : n} ${u}${n === 1 ? "" : "s"} ${sign}`;
    if (abs < 60) return diff < 0 ? "just now" : "in <1 min";
    if (abs < 3600) return fmt(Math.round(abs / 60), "min");
    if (abs < 86400) return fmt(Math.round(abs / 3600), "hour");
    if (abs < 86400 * 7) return fmt(Math.round(abs / 86400), "day");
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

const DAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Build a short human-readable label for the recurrence rule, mirroring
 *  `recurrence.describe_recurrence` on the backend. */
function describeRecurrence(r) {
  if (!r || !r.repeat_type || r.repeat_type === "none") return "";
  const h = r.repeat_hour ?? 0;
  const m = r.repeat_minute ?? 0;
  const h12 = ((h - 1 + 12) % 12) + 1;
  const ap = h < 12 ? "AM" : "PM";
  const time = `${h12}:${String(m).padStart(2, "0")} ${ap}`;
  const interval = r.repeat_interval || 1;
  if (r.repeat_type === "daily") {
    const prefix = interval === 1 ? "Daily" : `Every ${interval} days`;
    return `${prefix} · ${time}`;
  }
  if (r.repeat_type === "weekly") {
    const days = Array.isArray(r.repeat_days_of_week) ? r.repeat_days_of_week : [];
    if (!days.length) return "";
    const sorted = [...new Set(days.filter((d) => d >= 0 && d <= 6))].sort((a, b) => a - b);
    const dayStr = sorted.map((d) => DAY_SHORT[d]).join(" · ");
    const prefix = interval === 1 ? "" : `Every ${interval} weeks · `;
    return `${prefix}${dayStr} · ${time}`;
  }
  if (r.repeat_type === "monthly") {
    // V9.48 — Monthly recurrence: list of days-of-month (1..31).
    // Mirrors backend `describe_recurrence` output ("Monthly · 1st,
    // 15th · 6:00 AM").
    const days = Array.isArray(r.repeat_days_of_month) ? r.repeat_days_of_month : [];
    if (!days.length) return "";
    const sorted = [...new Set(days.filter((d) => d >= 1 && d <= 31))].sort((a, b) => a - b);
    const ord = (n) => {
      const mod100 = n % 100;
      if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
      const mod10 = n % 10;
      return `${n}${mod10 === 1 ? "st" : mod10 === 2 ? "nd" : mod10 === 3 ? "rd" : "th"}`;
    };
    const dayStr = sorted.map(ord).join(", ");
    const prefix = interval === 1 ? "Monthly" : `Every ${interval} months`;
    return `${prefix} · ${dayStr} · ${time}`;
  }
  return "";
}

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

function fromLocalInput(local) {
  if (!local) return null;
  return new Date(local).toISOString();
}

const EMPTY_FORM = {
  title: "",
  desc: "",
  when: "",
  repeat_type: "none", // none | daily | weekly | monthly
  repeat_hour: 9,
  repeat_minute: 0,
  repeat_days_of_week: [],
  repeat_days_of_month: [],
};

export default function Reminders() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const defaultWhen = toLocalInput(new Date(Date.now() + 60 * 60 * 1000).toISOString());

  // Create flow uses persistent draft. Edit flow uses transient state.
  const [draft, setDraft, clearDraft] = useDraft("reminder-new", {
    ...EMPTY_FORM,
    when: defaultWhen,
  });
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const form = editingId ? editForm : draft;
  const setForm = editingId
    ? (updater) =>
        setEditForm((f) => (typeof updater === "function" ? updater(f) : updater))
    : (updater) =>
        setDraft((d) => (typeof updater === "function" ? updater(d) : updater));

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Auto-open the create panel if a draft was restored on mount
    if (
      (draft.title && draft.title.trim()) ||
      (draft.desc && draft.desc.trim())
    ) {
      setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    try {
      const list = await API.listReminders();
      setItems(list);
    } catch {
      toast.error("Failed to load reminders");
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setOpen(true);
  };

  const openEdit = (rem) => {
    setEditingId(rem.id);
    setEditForm({
      title: rem.title || "",
      desc: rem.description || "",
      when: rem.remind_at ? toLocalInput(rem.remind_at) : "",
      repeat_type: rem.repeat_type || "none",
      repeat_hour: rem.repeat_hour ?? 9,
      repeat_minute: rem.repeat_minute ?? 0,
      repeat_days_of_week: rem.repeat_days_of_week || [],
      repeat_days_of_month: rem.repeat_days_of_month || [],
    });
    setOpen(true);
  };

  const closeForm = () => {
    setOpen(false);
    setEditingId(null);
  };

  const buildPayload = () => {
    const base = {
      title: form.title.trim(),
      description: (form.desc || "").trim(),
    };
    if (form.repeat_type === "none") {
      return {
        ...base,
        remind_at: fromLocalInput(form.when),
        repeat_type: "none",
        repeat_days_of_week: [],
        repeat_hour: null,
        repeat_minute: null,
      };
    }
    // recurring
    return {
      ...base,
      repeat_type: form.repeat_type,
      repeat_hour: Number(form.repeat_hour),
      repeat_minute: Number(form.repeat_minute),
      repeat_days_of_week:
        form.repeat_type === "weekly" ? [...(form.repeat_days_of_week || [])].sort() : [],
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || undefined,
    };
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (form.repeat_type === "none" && !form.when) {
      toast.error("Pick a time for the reminder");
      return;
    }
    if (form.repeat_type === "weekly" && (form.repeat_days_of_week || []).length === 0) {
      toast.error("Pick at least one weekday");
      return;
    }
    if (form.repeat_type === "monthly" && (form.repeat_days_of_month || []).length === 0) {
      toast.error("Pick at least one day of the month");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await API.updateReminder(editingId, buildPayload());
        toast.success("Reminder updated");
      } else {
        await API.createReminder(buildPayload());
        toast.success("Reminder set");
      }
      closeForm();
      if (!editingId) {
        setDraft({ ...EMPTY_FORM, when: defaultWhen });
        clearDraft();
      }
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to save reminder";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (rem) => {
    const isSeries = rem.repeat_type && rem.repeat_type !== "none";
    const confirmMsg = isSeries
      ? `Delete the entire "${rem.title}" recurring series?`
      : `Delete "${rem.title}"?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await API.deleteReminder(rem.id);
      setItems((x) => x.filter((r) => r.id !== rem.id));
      toast.success(isSeries ? "Series deleted" : "Reminder deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const markDone = async (id, status = "done") => {
    try {
      await API.updateReminder(id, { status });
      await load();
    } catch {
      toast.error("Failed to update");
    }
  };

  const snooze = async (id, minutes) => {
    try {
      await API.snoozeReminder(id, minutes);
      await load();
      toast.success(`Snoozed ${minutes < 60 ? `${minutes} min` : `${minutes / 60} h`}`);
    } catch {
      toast.error("Failed to snooze");
    }
  };

  const pause = async (id) => {
    try {
      await API.pauseReminder(id);
      await load();
      toast.success("Paused");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to pause";
      toast.error(msg);
    }
  };

  const resume = async (id) => {
    try {
      await API.resumeReminder(id);
      await load();
      toast.success("Resumed");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to resume";
      toast.error(msg);
    }
  };

  const skipNext = async (rem) => {
    if (
      !window.confirm(
        `Skip the next occurrence of "${rem.title}"? The series will continue normally after that.`
      )
    )
      return;
    try {
      await API.skipNextReminder(rem.id);
      await load();
      toast.success("Skipped this occurrence");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to skip";
      toast.error(msg);
    }
  };

  const upcoming = useMemo(
    () => items.filter((i) => i.status === "pending"),
    [items]
  );
  const paused = useMemo(
    () => items.filter((i) => i.status === "paused"),
    [items]
  );
  const past = useMemo(
    () => items.filter((i) => i.status !== "pending" && i.status !== "paused"),
    [items]
  );

  // Highlight machinery (unchanged from previous version)
  const [highlightId, setHighlightId] = useState(null);
  const rowRefs = useRef({});
  const triggerHighlight = useCallback((id) => {
    setHighlightId(id);
    requestAnimationFrame(() => {
      const el = rowRefs.current[id];
      if (el) {
        try {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        } catch {}
      }
    });
  }, []);
  useEffect(() => {
    if (!highlightId) return;
    const t = setTimeout(() => setHighlightId(null), 2400);
    return () => clearTimeout(t);
  }, [highlightId]);
  useEffect(() => {
    const onCreated = () => load();
    window.addEventListener("journey:reminder-created", onCreated);
    return () => window.removeEventListener("journey:reminder-created", onCreated);
  }, []);
  useEffect(() => {
    const onFired = (e) => {
      const id = e.detail?.reminderId;
      if (!id) return;
      load();
      triggerHighlight(id);
    };
    window.addEventListener("journey:reminder-fired", onFired);
    return () => window.removeEventListener("journey:reminder-fired", onFired);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerHighlight]);
  useEffect(() => {
    if (highlightId) return;
    const recent = items
      .filter((r) => r.status === "pending" && r.notified === 0 && r.last_notified_at)
      .filter((r) => Date.now() - new Date(r.last_notified_at).getTime() < 8000)
      .sort((a, b) => new Date(b.last_notified_at) - new Date(a.last_notified_at))[0];
    if (recent) triggerHighlight(recent.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  return (
    <div className="overflow-y-auto scroll-contain" data-testid="reminders-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <div className="flex items-start justify-between gap-4 mb-7">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shrink-0">
              <Bell size={22} weight="duotone" className="text-cyan-400" />
            </div>
            <div>
              <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                Reminders
              </h1>
              <p className="text-white/60 text-sm mt-1">
                Schedule one-time and recurring alerts. Journey notifies you in-app and via system push.
              </p>
            </div>
          </div>
          <Button
            onClick={openCreate}
            className="bg-cyan-500 hover:bg-cyan-400 text-black font-medium rounded-xl shrink-0"
            data-testid="reminder-new-button"
          >
            <Plus size={16} weight="bold" className="mr-1" />
            New
          </Button>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="rounded-2xl bg-[#0f0f0f] border border-white/5 p-4 mb-6"
              data-testid="reminder-form-panel"
            >
              <ReminderForm
                form={form}
                setForm={setForm}
                editing={!!editingId}
                onSave={save}
                onCancel={closeForm}
                saving={saving}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <Section title="Upcoming" empty="No upcoming reminders.">
          {upcoming.map((r) => (
            <ReminderRow
              key={r.id}
              r={r}
              onDone={markDone}
              onDelete={remove}
              onSnooze={snooze}
              onEdit={openEdit}
              onPause={pause}
              onResume={resume}
              onSkipNext={skipNext}
              highlight={highlightId === r.id}
              rowRef={(el) => {
                if (el) rowRefs.current[r.id] = el;
                else delete rowRefs.current[r.id];
              }}
            />
          ))}
        </Section>

        {paused.length > 0 && (
          <div className="mt-8" data-testid="reminders-paused-section">
            <Section title="Paused" empty="">
              {paused.map((r) => (
                <ReminderRow
                  key={r.id}
                  r={r}
                  onDone={markDone}
                  onDelete={remove}
                  onSnooze={snooze}
                  onEdit={openEdit}
                  onPause={pause}
                  onResume={resume}
                  onSkipNext={skipNext}
                  highlight={highlightId === r.id}
                  rowRef={(el) => {
                    if (el) rowRefs.current[r.id] = el;
                    else delete rowRefs.current[r.id];
                  }}
                />
              ))}
            </Section>
          </div>
        )}

        <ScheduleActivityLog />

        {past.length > 0 && (
          <div className="mt-8">
            <Section title="History" empty="">
              {past.map((r) => (
                <ReminderRow
                  key={r.id}
                  r={r}
                  onDone={markDone}
                  onDelete={remove}
                  onSnooze={snooze}
                  onEdit={openEdit}
                  onPause={pause}
                  onResume={resume}
                  onSkipNext={skipNext}
                  highlight={highlightId === r.id}
                  rowRef={(el) => {
                    if (el) rowRefs.current[r.id] = el;
                    else delete rowRefs.current[r.id];
                  }}
                />
              ))}
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, empty, children }) {
  const isEmpty = Array.isArray(children) ? children.length === 0 : !children;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-medium mb-3">
        {title}
      </div>
      {isEmpty ? (
        empty ? <div className="text-sm text-white/40 px-1">{empty}</div> : null
      ) : (
        <ul className="space-y-2.5">{children}</ul>
      )}
    </div>
  );
}

/** New reminder + edit reminder form. Shared between create and edit
 *  flows — they only differ in which state setter we hand in. */
function ReminderForm({ form, setForm, editing, onSave, onCancel, saving }) {
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleDay = (idx) =>
    setForm((f) => {
      const cur = new Set(f.repeat_days_of_week || []);
      cur.has(idx) ? cur.delete(idx) : cur.add(idx);
      return { ...f, repeat_days_of_week: [...cur].sort() };
    });
  const toggleMonthDay = (day) =>
    setForm((f) => {
      const cur = new Set(f.repeat_days_of_month || []);
      cur.has(day) ? cur.delete(day) : cur.add(day);
      return { ...f, repeat_days_of_month: [...cur].sort((a, b) => a - b) };
    });

  const repeatType = form.repeat_type || "none";

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-medium">
          {editing ? "Edit reminder" : "New reminder"}
        </h3>
        <button
          onClick={onCancel}
          className="p-2 -m-1 text-white/50 hover:text-white"
          aria-label="Close"
          data-testid="reminder-close"
        >
          <X size={18} />
        </button>
      </div>
      <div className="space-y-3">
        <Input
          placeholder="Title"
          value={form.title || ""}
          onChange={(e) => setField("title", e.target.value)}
          className="bg-transparent border-white/10 focus-visible:ring-cyan-500/40"
          data-testid="reminder-title-input"
        />
        <Textarea
          placeholder="Description (optional)"
          value={form.desc || ""}
          onChange={(e) => setField("desc", e.target.value)}
          className="bg-transparent border-white/10 focus-visible:ring-cyan-500/40 min-h-[60px]"
          data-testid="reminder-desc-input"
        />

        {/* Repeat selector */}
        <div>
          <label className="text-xs text-white/50 mb-1.5 block">Repeats</label>
          <div
            className="inline-flex rounded-lg border border-white/10 p-0.5 gap-0.5"
            data-testid="reminder-repeat-toggle"
          >
            {[
              { key: "none", label: "One time" },
              { key: "daily", label: "Daily" },
              { key: "weekly", label: "Weekly" },
              { key: "monthly", label: "Monthly" },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setField("repeat_type", opt.key)}
                className={`px-3 py-1.5 text-xs rounded-md transition ${
                  repeatType === opt.key
                    ? "bg-cyan-500 text-black font-medium"
                    : "text-white/70 hover:text-white"
                }`}
                data-testid={`reminder-repeat-${opt.key}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {repeatType === "none" ? (
          <div>
            <label className="text-xs text-white/50 mb-1 block">When</label>
            <input
              type="datetime-local"
              value={form.when || ""}
              onChange={(e) => setField("when", e.target.value)}
              className="w-full bg-transparent border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/40 [color-scheme:dark]"
              data-testid="reminder-time-input"
            />
          </div>
        ) : (
          <>
            {repeatType === "weekly" && (
              <div>
                <label className="text-xs text-white/50 mb-1.5 block">On these days</label>
                <div
                  className="flex flex-wrap gap-1.5"
                  data-testid="reminder-weekday-picker"
                >
                  {DAY_SHORT.map((d, idx) => {
                    const active = (form.repeat_days_of_week || []).includes(idx);
                    return (
                      <button
                        key={d}
                        onClick={() => toggleDay(idx)}
                        className={`px-3 py-1.5 text-xs rounded-md border transition ${
                          active
                            ? "bg-cyan-500 text-black border-cyan-500 font-medium"
                            : "text-white/70 border-white/10 hover:border-white/30"
                        }`}
                        data-testid={`reminder-weekday-${idx}`}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {repeatType === "monthly" && (
              <div>
                <label className="text-xs text-white/50 mb-1.5 block">
                  On these days of the month
                </label>
                <div
                  className="grid grid-cols-7 gap-1.5 max-w-md"
                  data-testid="reminder-monthday-picker"
                >
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => {
                    const active = (form.repeat_days_of_month || []).includes(day);
                    return (
                      <button
                        key={day}
                        onClick={() => toggleMonthDay(day)}
                        className={`h-9 text-xs rounded-md border transition ${
                          active
                            ? "bg-cyan-500 text-black border-cyan-500 font-medium"
                            : "text-white/70 border-white/10 hover:border-white/30"
                        }`}
                        data-testid={`reminder-monthday-${day}`}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
                <div className="text-[10px] text-white/40 mt-1.5">
                  Days that don&apos;t exist in a month (e.g. Feb 30) are
                  gracefully skipped.
                </div>
              </div>
            )}
            <div>
              <label className="text-xs text-white/50 mb-1 block">Time</label>
              <input
                type="time"
                value={`${String(form.repeat_hour ?? 9).padStart(2, "0")}:${String(
                  form.repeat_minute ?? 0
                ).padStart(2, "0")}`}
                onChange={(e) => {
                  const [hh, mm] = e.target.value.split(":");
                  setField("repeat_hour", parseInt(hh || "0", 10));
                  setField("repeat_minute", parseInt(mm || "0", 10));
                }}
                className="w-full bg-transparent border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/40 [color-scheme:dark]"
                data-testid="reminder-time-of-day-input"
              />
            </div>
          </>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button
            variant="ghost"
            onClick={onCancel}
            className="text-white/70"
            data-testid="reminder-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={saving}
            className="bg-cyan-500 hover:bg-cyan-400 text-black font-medium rounded-xl"
            data-testid="reminder-save"
          >
            {editing ? "Save changes" : "Save reminder"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ReminderRow({
  r,
  onDone,
  onDelete,
  onSnooze,
  onEdit,
  onPause,
  onResume,
  onSkipNext,
  highlight,
  rowRef,
}) {
  const isCancelled = r.status === "cancelled";
  const isDone = r.status === "done" || isCancelled;
  const isPaused = r.status === "paused";
  const isRecurring = r.repeat_type && r.repeat_type !== "none";
  const overdue = !isDone && !isPaused && new Date(r.remind_at) < new Date();

  const menuItems = [];
  if (!isDone) {
    menuItems.push({
      label: "Edit",
      icon: PencilSimple,
      onClick: () => onEdit(r),
      testId: `reminder-edit-${r.id}`,
    });
    if (isRecurring && !isPaused) {
      menuItems.push({
        label: "Skip next occurrence",
        icon: SkipForward,
        onClick: () => onSkipNext(r),
        testId: `reminder-skip-next-${r.id}`,
      });
      menuItems.push({
        label: "Pause series",
        icon: Pause,
        onClick: () => onPause(r.id),
        testId: `reminder-pause-${r.id}`,
      });
    }
    if (isRecurring && isPaused) {
      menuItems.push({
        label: "Resume series",
        icon: Play,
        onClick: () => onResume(r.id),
        testId: `reminder-resume-${r.id}`,
      });
    }
    if (!isPaused) {
      menuItems.push({
        label: "Mark done",
        icon: Check,
        onClick: () => onDone(r.id),
        testId: `reminder-done-${r.id}`,
      });
      menuItems.push({
        label: "Snooze 10 min",
        icon: Clock,
        onClick: () => onSnooze(r.id, 10),
        testId: `reminder-snooze-10-${r.id}`,
      });
      menuItems.push({
        label: "Snooze 1 hour",
        icon: Clock,
        onClick: () => onSnooze(r.id, 60),
        testId: `reminder-snooze-60-${r.id}`,
      });
    }
  } else {
    menuItems.push({
      label: "Reopen",
      icon: ArrowCounterClockwise,
      onClick: () => onDone(r.id, "pending"),
      testId: `reminder-reopen-${r.id}`,
    });
  }
  menuItems.push({ type: "separator" });
  menuItems.push({
    label: isRecurring ? "Delete series" : "Delete",
    icon: Trash,
    onClick: () => onDelete(r),
    destructive: true,
    testId: `reminder-delete-${r.id}`,
  });

  return (
    <motion.li
      ref={rowRef}
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl bg-[#0f0f0f] border border-white/5 px-4 py-3.5 flex items-start gap-3 ${
        highlight ? "memory-highlight" : ""
      } ${isPaused ? "opacity-70" : ""}`}
      data-testid={`reminder-item-${r.id}`}
    >
      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
          isDone
            ? "bg-emerald-500/10 border border-emerald-500/20"
            : isPaused
            ? "bg-white/5 border border-white/15"
            : overdue
            ? "bg-amber-500/10 border border-amber-500/20"
            : "bg-cyan-500/10 border border-cyan-500/20"
        }`}
      >
        {isDone ? (
          <Check size={14} weight="bold" className="text-emerald-400" />
        ) : isPaused ? (
          <PauseIcon size={14} weight="duotone" className="text-white/60" />
        ) : (
          <Clock
            size={14}
            weight="duotone"
            className={overdue ? "text-amber-400" : "text-cyan-400"}
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div
          className={`text-[15px] ${
            isDone ? "text-white/50 line-through" : "text-white/90"
          }`}
        >
          {r.title}
        </div>
        {r.description && (
          <div className="text-sm text-white/55 mt-0.5">{r.description}</div>
        )}
        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
          {/* V9.44.2 — Recipient badge: owner reminders push to YOU; */}
          {/* SMS-deferred reminders show the contact label so it's */}
          {/* obvious at-a-glance who the row is for. */}
          {r.sms_target_phone ? (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-500/12 text-sky-200 border border-sky-400/25 text-[11px]"
              data-testid={`reminder-recipient-contact-${r.id}`}
              title={`SMS to ${r.sms_target_label || "contact"}`}
            >
              <ChatCircleText size={11} weight="fill" />
              SMS to {r.sms_target_label || "contact"}
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-500/12 text-violet-200 border border-violet-400/25 text-[11px]"
              data-testid={`reminder-recipient-owner-${r.id}`}
              title="Owner reminder — push notification to you"
            >
              <User size={11} weight="fill" />
              For You · push
            </span>
          )}
          {isRecurring ? (
            <span
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/25 text-cyan-200 text-[11px]"
              data-testid={`reminder-recurrence-${r.id}`}
            >
              <ArrowsClockwise size={11} weight="bold" />
              <span>{describeRecurrence(r)}</span>
            </span>
          ) : null}
          {isPaused ? (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/8 border border-white/15 text-white/70 text-[11px]"
              data-testid={`reminder-paused-badge-${r.id}`}
            >
              <PauseIcon size={11} weight="bold" />
              <span>Paused</span>
            </span>
          ) : null}
          {r.created_by_label ? (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-400/25 text-amber-200 text-[11px]"
              data-testid={`reminder-set-by-${r.id}`}
            >
              Set by {r.created_by_label}
            </span>
          ) : null}
          {r.last_changed_by_label && !isCancelled ? (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 border border-white/15 text-white/65 text-[11px]"
              data-testid={`reminder-changed-by-${r.id}`}
            >
              Changed by {r.last_changed_by_label}
            </span>
          ) : null}
          {isCancelled ? (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/10 border border-rose-400/25 text-rose-200 text-[11px]"
              data-testid={`reminder-cancelled-badge-${r.id}`}
            >
              Cancelled{r.cancelled_by_label ? ` by ${r.cancelled_by_label}` : ""}
            </span>
          ) : null}
        </div>
        <div className="text-xs text-white/40 mt-1">
          {isPaused
            ? "Paused — won't fire until resumed"
            : (isRecurring ? "Next " : "") + fmtRelative(r.remind_at)}
        </div>
      </div>
      <ItemMenu testId={`reminder-menu-${r.id}`} items={menuItems} />
    </motion.li>
  );
}
